import { Check, Search, Tag, Trash2 } from 'lucide-react'
import { useMemo, useState, type FormEvent } from 'react'
import { useProductData } from '../data'
import { authFetch } from '../auth'
import { skuTagAssignments } from '../segments'
import type { ProductLevel, ProductSegmentAssignment } from '../types'

const costBearerOptions = [
  { value: '', label: '未确认承担方' },
  { value: 'jd-platform', label: '京东平台承担' },
  { value: 'merchant', label: '商家承担' },
  { value: 'shared', label: '双方共同承担' },
]

export function ProductSegmentsPage() {
  const { data, segments, segmentIndex, selectedDate, reloadSegments } = useProductData()
  const activityTags = segments?.tags.filter((tag) => tag.kind === 'activity') || []
  const [tagId, setTagId] = useState(activityTags[0]?.id || '')
  const [productLevel, setProductLevel] = useState<ProductLevel>('spu')
  const [search, setSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [includeChildren, setIncludeChildren] = useState(true)
  const [startDate, setStartDate] = useState(selectedDate)
  const [endDate, setEndDate] = useState(selectedDate)
  const [activityName, setActivityName] = useState('')
  const [costBearer, setCostBearer] = useState('')
  const [note, setNote] = useState('')
  const [batchSkuText, setBatchSkuText] = useState('')
  const [batchMessage, setBatchMessage] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  const selectedTag = activityTags.find((tag) => tag.id === tagId) || activityTags[0]
  const products = useMemo(
    () => (data?.products || []).filter((product) => product.level === productLevel),
    [data, productLevel],
  )
  const visibleProducts = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const matches = needle
      ? products.filter((product) =>
          [product.name, product.sourceName, product.id, product.jdSkuId, product.merchantSku]
            .some((value) => value.toLowerCase().includes(needle)),
        )
      : products
    return matches.slice(0, 40)
  }, [products, search])
  const skuLookup = useMemo(() => {
    const lookup = new Map<string, string>()
    for (const product of data?.products || []) {
      if (product.level !== 'sku') continue
      for (const value of [product.id, product.jdSkuId]) {
        if (value) lookup.set(value, product.id)
      }
    }
    return lookup
  }, [data])
  const batchSkuResult = useMemo(() => {
    const lines = batchSkuText.split(/\r?\n/).map((value) => value.trim()).filter(Boolean)
    const uniqueCodes = Array.from(new Set(lines))
    const matchedIds: string[] = []
    const unmatchedCodes: string[] = []
    for (const code of uniqueCodes) {
      const id = skuLookup.get(code)
      if (id) matchedIds.push(id)
      else unmatchedCodes.push(code)
    }
    return {
      inputCount: lines.length,
      duplicateCount: lines.length - uniqueCodes.length,
      matchedIds: Array.from(new Set(matchedIds)),
      unmatchedCodes,
    }
  }, [batchSkuText, skuLookup])

  const tagSummaries = useMemo(() => {
    if (!data || !segments) return []
    const skus = data.products.filter((product) => product.level === 'sku')
    return activityTags.map((tag) => {
      const matchedSkus = skus.filter((sku) => skuTagAssignments(segmentIndex, sku.id, tag.id).length)
      return {
        tag,
        skuCount: matchedSkus.length,
        spuCount: new Set(matchedSkus.map((sku) => sku.spuId).filter(Boolean)).size,
      }
    })
  }, [activityTags, data, segmentIndex, segments])
  const activityAssignments = useMemo(
    () => segments?.assignments.filter((assignment) => activityTags.some((tag) => tag.id === assignment.tagId)) || [],
    [activityTags, segments],
  )

  const assignmentProductText = (assignment: ProductSegmentAssignment) => {
    const names = assignment.productIds.slice(0, 3).map((id) => {
      const product = data?.products.find((item) => item.level === assignment.productLevel && item.id === id)
      return product?.name || id
    })
    const remaining = assignment.productIds.length - names.length
    return `${names.join('、')}${remaining > 0 ? ` 等${assignment.productIds.length}个` : ''}`
  }

  const toggleProduct = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleLevel = (value: ProductLevel) => {
    setProductLevel(value)
    setSelectedIds(new Set())
    setSearch('')
  }

  const handleTag = (value: string) => {
    setTagId(value)
    const tag = segments?.tags.find((item) => item.id === value)
    if (tag?.kind === 'activity') {
      setStartDate((current) => current || selectedDate)
      setEndDate((current) => current || selectedDate)
    }
  }

  const addBatchSkus = () => {
    setMessage('')
    setError('')
    if (!batchSkuResult.inputCount) {
      setBatchMessage('请先粘贴SKU编码。')
      return
    }
    if (!batchSkuResult.matchedIds.length) {
      setBatchMessage('没有找到可标记的SKU，请检查编码。')
      return
    }
    const baseIds = productLevel === 'sku' ? selectedIds : new Set<string>()
    const nextIds = new Set(baseIds)
    let addedCount = 0
    for (const id of batchSkuResult.matchedIds) {
      if (!nextIds.has(id)) addedCount += 1
      nextIds.add(id)
    }
    setProductLevel('sku')
    setSearch('')
    setSelectedIds(nextIds)
    const matchedMessage = addedCount
      ? `已加入 ${addedCount} 个SKU`
      : `匹配的 ${batchSkuResult.matchedIds.length} 个SKU已在已选商品中`
    setBatchMessage(
      `${matchedMessage}${batchSkuResult.unmatchedCodes.length ? `，另有 ${batchSkuResult.unmatchedCodes.length} 个未匹配` : ''}。`,
    )
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setMessage('')
    setError('')
    if (!selectedTag || !selectedIds.size) {
      setError('请选择标签和至少一个商品。')
      return
    }
    setSaving(true)
    try {
      const response = await authFetch('/api/product-segments/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tagId: selectedTag.id,
          productLevel,
          productIds: Array.from(selectedIds),
          includeChildren: productLevel === 'spu' && includeChildren,
          startDate: selectedTag.kind === 'activity' ? startDate : '',
          endDate: selectedTag.kind === 'activity' ? endDate : '',
          activityName,
          costBearer,
          note,
        }),
      })
      const payload = await response.json() as { error?: string }
      if (!response.ok) throw new Error(payload.error || '保存失败')
      await reloadSegments()
      setSelectedIds(new Set())
      setBatchSkuText('')
      setBatchMessage('')
      setActivityName('')
      setNote('')
      setMessage(`已标记 ${selectedIds.size} 个${productLevel.toUpperCase()}。`)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setSaving(false)
    }
  }

  const deleteAssignment = async (assignment: ProductSegmentAssignment) => {
    if (!window.confirm(`确认删除这条“${segmentIndex.tagsById.get(assignment.tagId)?.name || assignment.tagId}”标记吗？`)) return
    setMessage('')
    setError('')
    try {
      const response = await authFetch(`/api/product-segments/assignments/${encodeURIComponent(assignment.id)}`, { method: 'DELETE' })
      const payload = await response.json() as { error?: string }
      if (!response.ok) throw new Error(payload.error || '删除失败')
      await reloadSegments()
      setMessage('标记已删除。')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    }
  }

  if (!data || !segments || !selectedTag) return null
  return (
    <div className="page-stack">
      <div className="page-title-row">
        <div>
          <h1>活动记录</h1>
          <p>记录特价、五折等活动的商品范围和有效期；资料只保存在本机。</p>
        </div>
      </div>

      <section className="segment-summary-grid" aria-label="活动记录概览">
        {tagSummaries.map(({ tag: item, spuCount, skuCount }) => (
          <article key={item.id} className="segment-summary-card">
            <span className={`tag-badge tag-${item.tone}`}><Tag aria-hidden="true" />{item.name}</span>
            <strong>{spuCount.toLocaleString('zh-CN')} SPU</strong>
            <span>{skuCount.toLocaleString('zh-CN')} 个官方映射SKU</span>
          </article>
        ))}
      </section>

      <section className="segment-editor-grid">
        <form className="segment-form" onSubmit={handleSubmit}>
          <div className="section-heading-row">
            <div><h2>新增活动记录</h2><p>活动必须填写有效期；SPU可按官方映射同步到当前SKU。</p></div>
          </div>
          <div className="segment-form-fields">
            <label>
              <span>标签</span>
              <select value={selectedTag.id} onChange={(event) => handleTag(event.target.value)}>
                {activityTags.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
              </select>
            </label>
            <label>
              <span>商品层级</span>
              <select value={productLevel} onChange={(event) => handleLevel(event.target.value as ProductLevel)}>
                <option value="spu">SPU</option>
                <option value="sku">SKU</option>
              </select>
            </label>
            {selectedTag.kind === 'activity' && (
              <>
                <label><span>活动开始</span><input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} required /></label>
                <label><span>活动结束</span><input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} required /></label>
                <label><span>活动名称</span><input value={activityName} onChange={(event) => setActivityName(event.target.value)} placeholder="例如：7月五折专场" /></label>
                <label>
                  <span>优惠承担</span>
                  <select value={costBearer} onChange={(event) => setCostBearer(event.target.value)}>
                    {costBearerOptions.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                  </select>
                </label>
              </>
            )}
          </div>
          {productLevel === 'spu' && (
            <label className="checkbox-line">
              <input type="checkbox" checked={includeChildren} onChange={(event) => setIncludeChildren(event.target.checked)} />
              同步标记该SPU当前官方映射的全部SKU
            </label>
          )}
          <label className="segment-note-field"><span>备注</span><textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="记录活动来源、报名范围或需要复核的口径" /></label>

          <div className="segment-product-picker">
            <section className="batch-sku-panel" aria-labelledby="batch-sku-title">
              <div className="batch-sku-heading">
                <div>
                  <h3 id="batch-sku-title">批量输入SKU</h3>
                  <p>一行一个京东SKU编码；校验后自动切换为SKU层级并加入已选商品。</p>
                </div>
                {batchSkuResult.inputCount > 0 && (
                  <span>{batchSkuResult.inputCount.toLocaleString('zh-CN')} 行</span>
                )}
              </div>
              <label className="batch-sku-field">
                <span className="sr-only">批量输入SKU编码</span>
                <textarea
                  value={batchSkuText}
                  onChange={(event) => {
                    setBatchSkuText(event.target.value)
                    setBatchMessage('')
                  }}
                  placeholder={'例如：\n10177285370028\n10177285370029\n10177285370030'}
                  spellCheck={false}
                />
              </label>
              {batchSkuResult.inputCount > 0 && (
                <div className="batch-sku-summary" aria-live="polite">
                  <span>匹配 <strong>{batchSkuResult.matchedIds.length}</strong></span>
                  <span>重复 <strong>{batchSkuResult.duplicateCount}</strong></span>
                  <span className={batchSkuResult.unmatchedCodes.length ? 'has-error' : ''}>
                    未匹配 <strong>{batchSkuResult.unmatchedCodes.length}</strong>
                  </span>
                </div>
              )}
              {!!batchSkuResult.unmatchedCodes.length && (
                <p className="batch-sku-unmatched">
                  未匹配：{batchSkuResult.unmatchedCodes.slice(0, 8).join('、')}
                  {batchSkuResult.unmatchedCodes.length > 8 ? ` 等${batchSkuResult.unmatchedCodes.length}个` : ''}
                </p>
              )}
              {batchMessage && <p className="batch-sku-message">{batchMessage}</p>}
              <div className="batch-sku-actions">
                <button
                  type="button"
                  className="batch-sku-primary"
                  onClick={addBatchSkus}
                  disabled={!batchSkuResult.matchedIds.length}
                >
                  校验并加入已选SKU
                </button>
                <button
                  type="button"
                  className="batch-sku-clear"
                  onClick={() => {
                    setBatchSkuText('')
                    setBatchMessage('')
                  }}
                  disabled={!batchSkuText}
                >
                  清空
                </button>
              </div>
            </section>
            <label className="search-control">
              <span className="sr-only">搜索待标记商品</span><Search aria-hidden="true" />
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`搜索${productLevel.toUpperCase()}名称或编码`} />
            </label>
            <div className="segment-picker-heading">
              <span>最多展示前40项</span>
              <strong>已选择 {selectedIds.size} 项</strong>
            </div>
            <div className="segment-product-options">
              {visibleProducts.map((product) => (
                <label key={`${product.level}:${product.id}`} className={selectedIds.has(product.id) ? 'is-selected' : ''}>
                  <input type="checkbox" checked={selectedIds.has(product.id)} onChange={() => toggleProduct(product.id)} />
                  {product.image ? <img src={product.image} alt="" loading="lazy" /> : <span className="picker-image-fallback">无图</span>}
                  <span className="picker-product-copy"><strong>{product.name}</strong><small>{product.level.toUpperCase()} {product.id}</small></span>
                  {selectedIds.has(product.id) && <Check aria-hidden="true" />}
                </label>
              ))}
              {!visibleProducts.length && <div className="empty-state">没有匹配商品。</div>}
            </div>
          </div>
          {message && <p className="form-success">{message}</p>}
          {error && <p className="form-error">{error}</p>}
          <button type="submit" className="primary-button" disabled={saving || !selectedIds.size}>
            {saving ? '正在保存…' : '保存活动记录'}
          </button>
        </form>

        <section className="segment-assignment-panel">
          <div className="section-heading-row">
            <div><h2>现有活动记录</h2><p>共 {activityAssignments.length} 条记录</p></div>
          </div>
          <div className="segment-assignment-list">
            {activityAssignments.map((assignment) => {
              const tag = segmentIndex.tagsById.get(assignment.tagId)
              const period = assignment.startDate
                ? `${assignment.startDate} 至 ${assignment.endDate}`
                : '长期有效'
              return (
                <article key={assignment.id}>
                  <div>
                    <span className={`tag-badge tag-${tag?.tone || 'gray'}`}>{tag?.name || assignment.tagId}</span>
                    <strong>{assignment.productLevel.toUpperCase()} · {assignment.productIds.length}项</strong>
                  </div>
                  <p>{assignmentProductText(assignment)}</p>
                  <span>{period}{assignment.activityName ? ` · ${assignment.activityName}` : ''}</span>
                  <small>{assignment.note || assignment.source}</small>
                  <button type="button" onClick={() => deleteAssignment(assignment)} aria-label="删除标记"><Trash2 aria-hidden="true" /></button>
                </article>
              )
            })}
            {!activityAssignments.length && <div className="empty-state">还没有活动记录。</div>}
          </div>
        </section>
      </section>
    </div>
  )
}
